"use client";

import { Ico20ArrowRight } from "@/shared/assets/icons/ico_20_arrow_right";
import Link from "next/link";
import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import { Swiper as SwiperType } from "swiper";
import "swiper/css";

const contentData = [
  {
    image: "/images/home/MPS_01.webp",
    title: "mn_pd_pn_2_title_1",
    descriptions: ["mn_pd_pn_2_context_1_1", "mn_pd_pn_2_context_1_2"],
    activeTag: 0,
  },
  {
    image: "/images/home/MPS_02.webp",
    title: "mn_pd_pn_2_title_2",
    descriptions: ["mn_pd_pn_2_context_2"],
    activeTag: 1,
  },
  {
    image: "/images/home/MPS_03.webp",
    title: "mn_pd_pn_2_title_3",
    descriptions: ["mn_pd_pn_2_context_3"],
    activeTag: 2,
  },
  {
    image: "/images/home/MPS_04.webp",
    title: "mn_pd_pn_2_title_4",
    descriptions: ["mn_pd_pn_2_context_4_1", "mn_pd_pn_2_context_4_2"],
    activeTag: 3,
  },
];

const tags = ["mn_pd_pn_2_title_1", "mn_pd_pn_2_title_2", "mn_pd_pn_2_title_3", "mn_pd_pn_2_title_4"];

const mdsContentData = [
  {
    image: "/images/home/MDS_01.webp",
    title: "mn_pd_pn_3_title_1",
    descriptions: ["mn_pd_pn_3_context_1_1", "mn_pd_pn_3_context_1_2"],
    activeTag: 0,
  },
  {
    image: "/images/home/MDS_02.webp",
    title: "mn_pd_pn_3_title_2",
    descriptions: ["mn_pd_pn_3_context_2_1", "mn_pd_pn_3_context_2_2"],
    activeTag: 1,
  },
  {
    image: "/images/home/MDS_03.webp",
    title: "mn_pd_pn_3_title_3",
    descriptions: ["mn_pd_pn_3_context_3_1", "mn_pd_pn_3_context_3_2"],
    activeTag: 2,
  },
  {
    image: "/images/home/MDS_04.webp",
    title: "mn_pd_pn_3_title_4",
    descriptions: [
      "mn_pd_pn_3_context_4_1",
      "mn_pd_pn_3_context_4_2",
      "mn_pd_pn_3_context_4_3",
      "mn_pd_pn_3_context_4_4",
    ],
    activeTag: 3,
  },
];

const mdsTags = ["mn_pd_pn_3_title_1", "mn_pd_pn_3_title_2", "mn_pd_pn_3_title_3", "mn_pd_pn_3_title_4"];

export function SoftwareMobile() {
  const t = useTranslations();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mdsCurrentIndex, setMdsCurrentIndex] = useState(0);

  // 스크롤 dim 상태
  const [showLeftDim, setShowLeftDim] = useState(false);
  const [showRightDim, setShowRightDim] = useState(true);
  const [showMdsLeftDim, setShowMdsLeftDim] = useState(false);
  const [showMdsRightDim, setShowMdsRightDim] = useState(true);

  const tagsRef = useRef<HTMLDivElement>(null);
  const mdsTagsRef = useRef<HTMLDivElement>(null);
  const mpsSwiper = useRef<SwiperType | null>(null);
  const mdsSwiper = useRef<SwiperType | null>(null);

  const handleTagClick = (index: number, isMds: boolean = false) => {
    if (isMds) {
      mdsSwiper.current?.slideTo(index);
    } else {
      mpsSwiper.current?.slideTo(index);
    }
  };

  const scrollToActiveTag = (index: number, isMds: boolean = false) => {
    const tagsContainer = isMds ? mdsTagsRef.current : tagsRef.current;
    if (tagsContainer) {
      const activeTag = tagsContainer.children[index] as HTMLElement;
      if (activeTag) {
        const containerWidth = tagsContainer.offsetWidth;
        const tagWidth = activeTag.offsetWidth;
        const tagLeft = activeTag.offsetLeft;
        const scrollLeft = tagLeft - containerWidth / 2 + tagWidth / 2;

        tagsContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: "smooth",
        });

        // Update dim states
        setTimeout(() => {
          const element = tagsContainer;
          const scrollLeft = element.scrollLeft;
          const scrollWidth = element.scrollWidth;
          const clientWidth = element.clientWidth;

          if (isMds) {
            setShowMdsLeftDim(scrollLeft > 0);
            setShowMdsRightDim(scrollLeft < scrollWidth - clientWidth - 1);
          } else {
            setShowLeftDim(scrollLeft > 0);
            setShowRightDim(scrollLeft < scrollWidth - clientWidth - 1);
          }
        }, 300);
      }
    }
  };

  const handleMpsSlideChange = (swiper: SwiperType) => {
    setCurrentIndex(swiper.realIndex);
    scrollToActiveTag(swiper.realIndex, false);
  };

  const handleMdsSlideChange = (swiper: SwiperType) => {
    setMdsCurrentIndex(swiper.realIndex);
    scrollToActiveTag(swiper.realIndex, true);
  };

  return (
    <>
      <div className="m-auto flex w-full max-w-[1320px] flex-col items-center justify-center bg-white px-4">
        <div className="mb-5 flex w-full items-end justify-between">
          <div className="flex flex-col items-start gap-1">
            <span className="heading-1 text-gray-900">MPS</span>
            <span className="heading-4-regular max-w-[198px] text-gray-500">{t("mn_pd_pn_2_1")}</span>
          </div>

          <Link href="/solution?tab=mps" className="body-md-regular flex items-center gap-[6px] text-gray-700">
            {t("mn_pd_pn_2_btn")} <Ico20ArrowRight className="size-[18px]" />
          </Link>
        </div>

        {/* 모바일: 세로 배치 - 이미지가 위에, 텍스트가 아래에 */}
        <div className="flex w-full max-w-[1280px] flex-col gap-3">
          {/* 이미지 카드 - 위에 위치 */}
          <div className="relative min-h-[212px] w-full overflow-hidden rounded-[16px]">
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => (mpsSwiper.current = swiper)}
              onSlideChange={handleMpsSlideChange}
              direction="vertical"
              height={212}
              slidesPerView={1}
              spaceBetween={0}
              loop={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              className="h-[212px] w-full !overflow-hidden"
            >
              {contentData.map((content, index) => (
                <SwiperSlide key={index} className="opacity-100">
                  <div className="h-[212px] w-full rounded-[16px] bg-black">
                    <img
                      src={content.image}
                      alt={`MPS_${index + 1}`}
                      className="h-full w-full rounded-[16px] object-cover"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* 텍스트 카드 - 아래에 위치 */}
          <div className="relative flex min-h-[231px] w-full flex-col gap-8 rounded-[16px] border border-gray-100 bg-white px-6 pt-6 pb-10 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.05)]">
            {showLeftDim && (
              <div className="absolute top-5 left-0 z-10 h-10 w-12 bg-[linear-gradient(270deg,rgba(255,255,255,0)_0%,#FFF_79.33%)]" />
            )}
            <div ref={tagsRef} className="scrollbar-hide relative flex flex-row gap-[6px] overflow-x-auto">
              {tags.map((tag, index) => (
                <div
                  key={index}
                  onClick={() => handleTagClick(index, false)}
                  className={`body-md-medium w-fit cursor-pointer rounded-full px-[14px] py-[6px] whitespace-nowrap transition-colors duration-300 ${
                    index === currentIndex ? "bg-primary-100 text-primary-500 body-md-bold" : "bg-gray-50 text-gray-400"
                  }`}
                >
                  {t(tag)}
                </div>
              ))}
            </div>
            {showRightDim && (
              <div className="absolute top-5 right-0 z-10 h-10 w-12 bg-[linear-gradient(270deg,#FFF_20.67%,rgba(255,255,255,0)_100%)]" />
            )}
            <div className="flex flex-col gap-[6px]">
              <span className="heading-4-bold text-gray-900">{t(contentData[currentIndex].title)}</span>
              <div className="flex flex-col gap-1">
                {contentData[currentIndex].descriptions.map((desc, index) => (
                  <div key={index} className="flex items-start gap-[6px]">
                    <span className="body-lg-regular text-gray-500">·</span>
                    <span className="body-lg-regular text-gray-500">{t(desc)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MDS 섹션 - 모바일 버전 */}
      <div className="m-auto mt-15 flex w-full max-w-[1320px] flex-col items-center justify-center bg-white px-4">
        <div className="mb-5 flex w-full items-end justify-between">
          <div className="flex flex-col items-start">
            <span className="heading-1 text-gray-900">MDS</span>
            <span className="heading-4-regular max-w-[198px] text-gray-500">{t("mn_pd_pn_3_1")}</span>
          </div>

          <Link href="/solution?tab=mds" className="body-md-regular flex items-center gap-[6px] text-gray-700">
            {t("mn_pd_pn_3_btn")} <Ico20ArrowRight className="size-[18px]" />
          </Link>
        </div>

        {/* 모바일: 세로 배치 - 이미지가 위에, 텍스트가 아래에 */}
        <div className="flex w-full max-w-[1280px] flex-col gap-3">
          {/* 이미지 카드 - 위에 위치 */}
          <div className="relative min-h-[212px] w-full overflow-hidden rounded-[16px]">
            <Swiper
              modules={[Autoplay]}
              onSwiper={(swiper) => (mdsSwiper.current = swiper)}
              onSlideChange={handleMdsSlideChange}
              direction="vertical"
              height={212}
              slidesPerView={1}
              spaceBetween={0}
              loop={true}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              className="h-[212px] w-full !overflow-hidden"
            >
              {mdsContentData.map((content, index) => (
                <SwiperSlide key={index} className="opacity-100">
                  <div className="h-[212px] w-full rounded-[16px] bg-black">
                    <img
                      src={content.image}
                      alt={`MDS_${index + 1}`}
                      className="h-full w-full rounded-[16px] object-cover"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* 텍스트 카드 - 아래에 위치 */}
          <div className="relative flex min-h-[231px] w-full flex-col gap-8 rounded-[16px] border border-gray-100 bg-white px-6 pt-6 pb-10 shadow-[0px_10px_20px_0px_rgba(0,0,0,0.05)]">
            {showMdsLeftDim && (
              <div className="absolute top-5 left-0 z-10 h-10 w-12 bg-[linear-gradient(270deg,rgba(255,255,255,0)_0%,#FFF_79.33%)]" />
            )}
            <div ref={mdsTagsRef} className="scrollbar-hide relative flex flex-row gap-[6px] overflow-x-auto">
              {mdsTags.map((tag, index) => (
                <div
                  key={index}
                  onClick={() => handleTagClick(index, true)}
                  className={`body-md-medium w-fit cursor-pointer rounded-full px-[14px] py-[6px] whitespace-nowrap transition-colors duration-300 ${
                    index === mdsCurrentIndex
                      ? "bg-primary-100 text-primary-500 body-md-bold"
                      : "bg-gray-50 text-gray-400"
                  }`}
                >
                  {t(tag)}
                </div>
              ))}
            </div>
            {showMdsRightDim && (
              <div className="absolute top-5 right-0 z-10 h-10 w-12 bg-[linear-gradient(270deg,#FFF_20.67%,rgba(255,255,255,0)_100%)]" />
            )}
            <div className="flex flex-col gap-[6px]">
              <span className="heading-4-bold text-gray-900">{t(mdsContentData[mdsCurrentIndex].title)}</span>
              <div className="flex flex-col gap-1">
                {mdsContentData[mdsCurrentIndex].descriptions.map((desc, index) => (
                  <div key={index} className="flex items-start gap-[6px]">
                    <span className="body-lg-regular text-gray-500">·</span>
                    <span className="body-lg-regular text-gray-500">{t(desc)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
